import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function EarningsAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-320px)] sm:max-w-[calc(100vw-48px)] xs:max-w-[calc(100vw-24px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Earnings API" description="The Earnings API provides comprehensive earnings data for publicly traded companies,
          including historical quarterly performance, analyst estimates, earnings surprise trends,
          and..."  canonicalUrl="https://axionquant.com/docs/03-fundamentals/earnings/00-transcripts" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Earnings API</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          The Earnings API provides comprehensive earnings data for publicly traded companies,
          including historical quarterly performance, analyst estimates, earnings surprise trends,
          and detailed financial tables extracted directly from SEC 8-K earnings releases.
          All data is sourced from official company filings and real-time analyst surveys.
        </p>
      </div>


      <div className="space-y-12" ref={sectionRef}>
        {/* ========== 5. Earnings Transcript ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /earnings/:ticker/transcript
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Returns the full earnings call transcript for a specific fiscal quarter.
                Each paragraph is annotated with the speaker name and paragraph number,
                sourced from the official earnings conference call.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm"><span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">Required</span></td>
                      <td className="px-4 py-3 text-sm text-gray-700">Stock ticker symbol</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">year</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Fiscal year (e.g., 2024, 2025)
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">quarter</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer (1-4)</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Fiscal quarter (1, 2, 3, 4)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/earnings/AAPL/transcript?year=2024&quarter=3', '/earnings/MSFT/transcript?year=2024&quarter=4']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Key Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">paragraphNumber</p>
                  <p className="text-sm text-gray-600">Sequential paragraph number in the transcript</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">speaker</p>
                  <p className="text-sm text-gray-600">Name of the person speaking (e.g., &quot;Tim Cook&quot;, &quot;Analyst Name&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">content</p>
                  <p className="text-sm text-gray-600">Full text of the spoken paragraph</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Earnings Transcript (Apple Q2 2024)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Earnings Transcript (Apple Q2 2024)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="earnings/ticker/transcript"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response (abbreviated)</p>
                <Code id="03-fundamentals/earnings/transcript"></Code>
              </div>
            </div>
          </div>
        </div>

        {/* ========== 6. Transcript Sentiment ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /earnings/transcript/sentiment
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Analyzes the sentiment of an earnings call transcript, providing an overall sentiment label,
                confidence score, and a breakdown of positive and negative segments. The transcript is identified
                by a base64-encoded ID containing the ticker, fiscal year, and quarter.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">id</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Base64-encoded JSON object with ticker, year, and quarter
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/earnings/AAPL/transcript/sentiment?id=eyJ0aWNrZXIiOiJBQVBMIiwieWVhciI6MjAyNCwicXVhcnRlciI6NH0=']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Key Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">label</p>
                  <p className="text-sm text-gray-600">Overall sentiment label (POSITIVE or NEGATIVE)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">score</p>
                  <p className="text-sm text-gray-600">Confidence score (0.0 to 1.0)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">breakdown</p>
                  <div className="ml-2 mt-1 space-y-2">
                    <p className="text-xs text-gray-500">Contains positive and negative segment details</p>
                    <div className="border-l-4 border-gray-300 pl-3">
                      <p className="font-mono text-xs text-gray-800">positive.count <span className="text-gray-500">number</span></p>
                      <p className="text-xs text-gray-600 mb-1">Number of positive sentiment segments</p>
                      <p className="font-mono text-xs text-gray-800">positive.avgScore <span className="text-gray-500">number</span></p>
                      <p className="text-xs text-gray-600 mb-1">Average confidence score for positive segments</p>
                      <p className="font-mono text-xs text-gray-800">negative.count <span className="text-gray-500">number</span></p>
                      <p className="text-xs text-gray-600 mb-1">Number of negative sentiment segments</p>
                      <p className="font-mono text-xs text-gray-800">negative.avgScore <span className="text-gray-500">number</span></p>
                      <p className="text-xs text-gray-600 mb-1">Average confidence score for negative segments</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Transcript Sentiment (Apple Q2 2024)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Transcript Sentiment (Apple Q2 2024)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="earnings/transcript/sentiment"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="03-fundamentals/earnings/transcript-sentiment"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
